/**
 * <bodi-scroll-reveal> — viewport-triggered fade + translate + scale animation.
 *
 * Premium reveal animation with subtle blur, scale, and translate effects.
 * Uses double rAF pattern to ensure the initial hidden state is painted
 * before the transition begins.
 *
 * Usage:
 *   <bodi-scroll-reveal data-delay="0.2">
 *     <h2>Content appears with animation</h2>
 *   </bodi-scroll-reveal>
 *
 * Attributes:
 *   data-delay     — animation delay in seconds (default: 0)
 *   data-threshold — IntersectionObserver threshold (default: 0.15)
 *   data-distance  — translateY distance in px (default: 24)
 */
class BodiScrollReveal extends HTMLElement {
  connectedCallback() {
    /* Check for reduced motion preference */
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion) {
      /* Skip animation, just show content */
      this.style.setProperty('opacity', '1', 'important');
      return;
    }

    const distance = parseFloat(this.dataset.distance || '24');

    /* Use !important to override any conflicting styles */
    this.style.setProperty('opacity', '0', 'important');
    this.style.setProperty('transform', `translateY(${distance}px) scale(0.97)`, 'important');
    this.style.setProperty('filter', 'blur(4px)', 'important');
    this.style.setProperty('transition', 'none', 'important');
    this.style.setProperty('will-change', 'opacity, transform, filter', 'important');

    const delay = parseFloat(this.dataset.delay || '0');
    const threshold = parseFloat(this.dataset.threshold || '0.15');

    /* Custom easing — fast start, slow finish for organic feel */
    const easing = 'cubic-bezier(0.16, 1, 0.3, 1)';
    const duration = '0.9s';

    let revealed = false;
    const reveal = () => {
      if (revealed) return;
      revealed = true;
      this.style.setProperty('transition', `opacity ${duration} ${easing} ${delay}s, transform ${duration} ${easing} ${delay}s, filter ${duration} ${easing} ${delay}s`, 'important');
      this.style.setProperty('opacity', '1', 'important');
      this.style.setProperty('transform', 'translateY(0) scale(1)', 'important');
      this.style.setProperty('filter', 'blur(0)', 'important');
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            /* Double rAF guarantees the browser has painted the initial
               hidden state before we apply the transition. */
            requestAnimationFrame(() => requestAnimationFrame(reveal));
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold }
    );

    observer.observe(this);

    /* Failsafe: never leave content hidden if the observer doesn't fire
       (e.g. tab backgrounded at load, or IO quirks). Force reveal after 1.8s. */
    setTimeout(reveal, 1800);
  }
}

if (!customElements.get('bodi-scroll-reveal')) {
  customElements.define('bodi-scroll-reveal', BodiScrollReveal);
}
